<template>
  <transition-group
    tag="div"
    class="user-content-container"
    :name="!toggleBusiness ? 'slideback' : 'slide'"
  >
    <!-- Vista principal del menú -->
    <div v-if="!toggleBusiness" key="1" class="toggle-block">
      <!-- Selector de negocio -->
      <div class="ucp-business-card" @click="onToggleBusiness">
        <div class="ucp-biz-logo">
          <img
            :src="user.user_business.business_logo"
            alt=""
            v-if="user.user_business.business_logo && !mainLogoError"
            @error="mainLogoError = true"
          />
          <TextAvatarComponent
            :text="user.user_business.business_name"
            class="p-0"
            v-else
          />
        </div>
        <div class="ucp-biz-info">
          <span class="ucp-biz-name">{{
            user.user_business.business_name
          }}</span>
          <span class="ucp-biz-label">Cambiar negocio</span>
        </div>
        <i class="el-icon-arrow-right ucp-biz-arrow"></i>
      </div>

      <!-- Menú de opciones -->
      <nav class="ucp-menu">
        <!-- Multi-usuario: cada opción solo si el rol tiene acceso. -->
        <router-link
          v-if="$canOpen('profile.trade')"
          :to="{ path: '/dashboard/negocio', query: { action: 'editar' } }"
          class="ucp-menu-item"
        >
          <i class="el-icon-office-building"></i>
          <span>Negocio</span>
        </router-link>
        <router-link
          v-if="$store.getters.myRole !== 'member'"
          :to="{ name: 'plans' }"
          class="ucp-menu-item"
        >
          <i class="el-icon-medal"></i>
          <span>Actualizar plan</span>
        </router-link>
        <router-link
          :to="{ name: 'team' }"
          class="ucp-menu-item"
          v-if="$canOpen('team')"
        >
          <i class="el-icon-user"></i>
          <span>Equipo</span>
        </router-link>
        <router-link
          v-if="$canOpen('configurations')"
          :to="{ name: 'configurations' }"
          class="ucp-menu-item"
        >
          <i class="el-icon-setting"></i>
          <span>Configuración</span>
        </router-link>
        <router-link :to="{ name: 'soporte' }" class="ucp-menu-item">
          <i class="el-icon-headset"></i>
          <span>Soporte</span>
        </router-link>
        <div class="ucp-menu-divider"></div>
        <a
          href="#"
          class="ucp-menu-item ucp-menu-item--danger"
          @click.prevent="logout"
        >
          <i class="el-icon-switch-button"></i>
          <span>Cerrar sesión</span>
        </a>
      </nav>
    </div>

    <!-- Lista de negocios -->
    <div v-else key="2" class="toggle-block business-list">
      <div class="ucp-back" @click="onToggleBusiness">
        <i class="el-icon-arrow-left"></i>
        <span>Negocios</span>
      </div>

      <button
        class="ucp-add-biz"
        @click="goRoute('/dashboard/negocio', 'nuevo')"
      >
        <i class="el-icon-plus"></i> Nuevo negocio
      </button>

      <div class="ucp-biz-list">
        <div
          v-for="(item, index) in user.businesses"
          :key="index"
          :class="[
            'ucp-biz-list-item',
            { 'is-active': item.id == user.user_business.business_id }
          ]"
          @click="setBusinessToCurrentUser(item)"
        >
          <div class="ucp-biz-logo-sm">
            <img
              :src="item.business_logo"
              alt=""
              v-if="item.business_logo && !logoErrors[item.id]"
              @error="$set(logoErrors, item.id, true)"
            />
            <TextAvatarComponent :text="item.business_name" v-else />
          </div>
          <span class="ucp-biz-list-text">
            <span class="ucp-biz-list-name">{{ item.business_name }}</span>
            <span v-if="bizSubtitle(item)" class="ucp-biz-list-role">{{
              bizSubtitle(item)
            }}</span>
          </span>
          <i
            v-if="item.id == user.user_business.business_id"
            class="el-icon-check ucp-biz-check"
          ></i>
        </div>
      </div>
    </div>
  </transition-group>
</template>

<script>
import TextAvatarComponent from "../../../modules/dashboard/sales/components/TextAvatar";

export default {
  name: "UserContentPopover",
  data: () => ({
    toggleBusiness: false,
    mainLogoError: false,
    logoErrors: {}
  }),
  props: ["user"],
  components: {
    TextAvatarComponent
  },
  methods: {
    // RUC + rol compartido: distingue negocios con el mismo nombre.
    bizSubtitle(item) {
      const parts = [];
      if (item.business_ruc) parts.push(`RUC ${item.business_ruc}`);
      if (item.member_role && item.member_role !== "owner") {
        parts.push(
          item.member_role === "admin"
            ? "Compartido · Administrador"
            : "Compartido · Miembro"
        );
      }
      return parts.join(" · ");
    },
    onToggleBusiness() {
      this.toggleBusiness = !this.toggleBusiness;
    },
    async setBusinessToCurrentUser(business) {
      // Espera a que el negocio quede seleccionado (backend + store) antes
      // de navegar/forzar el refresh — si no se espera, el dashboard puede
      // llegar a pedir datos con el negocio anterior todavía activo.
      try {
        await this.$store.dispatch("userBusinessUpdated", business);
      } catch {
        // el interceptor global ya notifica el error al usuario
        return;
      }
      this.$forceUpdate();
    },
    goRoute(page, param) {
      this.$router
        .push({ path: page, query: { action: param } })
        .catch(() => {});
    },
    logout() {
      this.$store.dispatch("logout").then(() => {
        this.$router.push({ name: "login" });
      });
    }
  }
};
</script>

<style scoped lang="scss">
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.user-content-container {
  overflow: hidden;
  position: relative;
}

// El bloque visible queda en el flujo para que el contenedor crezca con sus
// opciones (sin cortes ni scroll interno); solo el que sale se superpone
// durante la animación.
.toggle-block {
  position: relative;
  width: 100%;
}

.slide-leave-active,
.slideback-leave-active {
  position: absolute;
  top: 0;
  left: 0;
}

// ── Business card selector ──
.ucp-business-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin: 4px 6px;
  border: 1px solid $border;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: darken($border, 10%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}
.ucp-biz-logo {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
}
.ucp-biz-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ucp-biz-name {
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ucp-biz-label {
  font-size: 11px;
  color: #909399;
}
.ucp-biz-arrow {
  font-size: 13px;
  color: #c0c4cc;
  flex-shrink: 0;
}

// El avatar de iniciales mide 40px por defecto: aquí llena su caja.
.ucp-biz-logo,
.ucp-biz-logo-sm {
  ::v-deep .avatar {
    width: 100%;
    height: 100%;
    border-radius: inherit;

    h4 {
      font-size: 12px;
      line-height: 1;
      letter-spacing: 0.02em;
    }
  }
}

// ── Menu items ──
.ucp-menu {
  display: flex;
  flex-direction: column;
  padding: 8px 6px;
}
.ucp-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.12s;
  cursor: pointer;

  i {
    font-size: 16px;
    color: #909399;
    width: 18px;
    text-align: center;
  }

  &:hover {
    background: $bg;
    color: $primary;
    text-decoration: none;

    i {
      color: $primary;
    }
  }

  &--danger {
    color: #e24b4a;

    i {
      color: #e24b4a;
    }

    &:hover {
      background: #fff0f0;
      color: #e24b4a;

      i {
        color: #e24b4a;
      }
    }
  }
}
.ucp-menu-divider {
  height: 1px;
  background: $border;
  margin: 4px 14px;
}

// ── Back button ──
.ucp-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin: 4px 6px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.12s;

  i {
    font-size: 16px;
    color: $primary;
  }

  span {
    font-size: 15px;
    font-weight: 600;
    color: $primary;
  }

  &:hover {
    background: $bg;
  }
}

// ── Add business ──
.ucp-add-biz {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: calc(100% - 12px);
  margin: 4px 6px 8px;
  padding: 9px;
  border: 1px dashed #c0c4cc;
  border-radius: 8px;
  background: transparent;
  color: #909399;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: $accent;
    color: $accent;
  }
}

// ── Business list ──
.ucp-biz-list {
  overflow-y: auto;
  max-height: 220px;
  padding: 0 6px;
}
.ucp-biz-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 2px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: $bg;
  }

  &.is-active {
    background: #f0fdf9;
    border-left: 3px solid $accent;
  }
}
.ucp-biz-logo-sm {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
.ucp-biz-list-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ucp-biz-check {
  color: $accent;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

// ── Slide animations ──
.ucp-biz-list-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.ucp-biz-list-role {
  font-size: 11px;
  color: #8a8fa8;
  margin-top: 2px;
}

.slide-leave-active,
.slide-enter-active {
  transition: 0.25s ease-in-out;
}
.slide-enter {
  transform: translate(100%, 0);
}
.slide-leave-to {
  transform: translate(-100%, 0);
}

.slideback-leave-active,
.slideback-enter-active {
  transition: 0.25s ease-in-out;
}
.slideback-enter {
  transform: translate(-100%, 0);
}
.slideback-leave-to {
  transform: translate(100%, 0);
}
</style>
